<script lang="ts">
    import { Button, Dropdown, DropdownShell, Dot } from "attractions";
    import Fa from "svelte-fa";
    import {
        faChevronDown,
        faPlusCircle,
        faMinusCircle,
    } from "@fortawesome/free-solid-svg-icons";
    import type { Puzzle } from "../types";
    import type { Writable } from "svelte/store";

    export let puzzle: Writable<Puzzle>;

    let featureRows: Map<string, number>;

    $: {
        featureRows = new Map();
        $puzzle.feats.forEach((featRow, i) => {
            featRow.forEach((feat) => {
                featureRows.set(feat, i);
            });
        });
    }

    const getFeaturesExceptRow = (row: number): string[] =>
        $puzzle.feats.filter((_, i) => i !== row).flat();

    const onAddConstraint = () => {
        const nextConsts = $puzzle.consts;
        nextConsts.push({ f1: "", f2: "", positive: true });
        $puzzle.consts = nextConsts;
    };

    const onRemoveConstraint = (i: number) => {
        $puzzle.consts = [
            ...$puzzle.consts.slice(0, i),
            ...$puzzle.consts.slice(i + 1),
        ];
    };
</script>

<div class="root-container">
    {#each $puzzle.consts as constraint, i}
        <div class="row-container">
            <Button
                noRipple
                danger
                disabled={$puzzle.featNames.length <= 2}
                on:click={() => onRemoveConstraint(i)}
                class="btn-no-margin"
            >
                <Fa icon={faMinusCircle} />
            </Button>

            <div class="left">
                <DropdownShell let:toggle>
                    <Button on:click={toggle} class="btn-no-margin">
                        <div class="btn-content-container">
                            {constraint.f1}
                            <div class="margin-left-md">
                                <Fa icon={faChevronDown} />
                            </div>
                        </div></Button
                    >
                    <Dropdown>
                        <div class="feat-dropdown-container">
                            {#each getFeaturesExceptRow(featureRows.get(constraint.f2)) as featureOption}
                                <Button
                                    class="btn-no-margin"
                                    on:click={() => {
                                        toggle();
                                        constraint.f1 = featureOption;
                                    }}
                                >
                                    <span class="margin-left-sm margin-right-sm"
                                        >{featureOption}</span
                                    >
                                </Button>
                            {/each}
                        </div>
                    </Dropdown>
                </DropdownShell>
            </div>

            <div
                class="constraint-type-toggle"
                on:click={() => (constraint.positive = !constraint.positive)}
            >
                <Dot
                    success={constraint.positive}
                    attention={!constraint.positive}
                />
            </div>

            <div class="right">
                <DropdownShell let:toggle>
                    <Button on:click={toggle} class="btn-no-margin">
                        <div class="btn-content-container">
                            <div class="margin-right-md">
                                <Fa icon={faChevronDown} />
                            </div>
                            {constraint.f2}
                        </div></Button
                    >
                    <Dropdown>
                        <div class="feat-dropdown-container">
                            {#each getFeaturesExceptRow(featureRows.get(constraint.f1)) as featureOption}
                                <Button
                                    class="btn-no-margin"
                                    on:click={() => {
                                        toggle();
                                        constraint.f2 = featureOption;
                                    }}
                                >
                                    {featureOption}
                                </Button>
                            {/each}
                        </div>
                    </Dropdown>
                </DropdownShell>
            </div>
        </div>
    {/each}

    <Button on:click={onAddConstraint}>
        <Fa icon={faPlusCircle} />
        <span class="margin-left-sm">Add Constraint</span>
    </Button>
</div>

<style lang="scss">
    @use "common.scss";
    @import "theme.scss";

    .row-container {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 0.25rem;
    }

    .feat-dropdown-container {
        max-height: 9rem;
        overflow-y: auto;
    }

    .root-container {
        width: 440px;
        height: 240px;
        padding: 1rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.125rem;
        overflow: auto;
    }

    .btn-content-container {
        width: 100%;
        display: flex;
        justify-content: space-between;
    }

    .constraint-type-toggle {
        cursor: pointer;
        padding: 0.25rem;
    }

    :global(.btn-no-margin) {
        margin: 0;
    }

    .left,
    .right {
        flex-grow: 1;
        flex-basis: 0;
        max-width: 10rem;
    }

    .left {
        display: flex;
        justify-content: end;
    }
</style>
